import i18next from 'i18next';

import { normalizeKeys } from '@commons/Excels/utils';
import normalizeStringValue from '@commons/utils/normalizeStringValue';

/** Sheet configs */

const getCommonColumns = () => ({
  ID: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.ID_COLUMN'),
  NAME: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.NAME_COLUMN'),
});

const getCommonSheetConfig = (normalizedColumns) => ({
  COLUMN_ID: { ID: 'id', NAME: 'name' },
  COLUMNS_CONFIG: normalizedColumns,
});

const getSupplierProductSheetConfig = () => {
  const columns = {
    ID: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.ID_COLUMN'),
    NAME: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.NAME_COLUMN'),
    PACKAGING_NAME_1: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.PACKAGING_NAME_1_COLUMN'),
    PACKAGING_NAME_2: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.PACKAGING_NAME_2_COLUMN'),
    PACKAGING_NAME_3: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.PACKAGING_NAME_3_COLUMN'),
  };

  const normalizedColumns = normalizeKeys(columns);

  return {
    SHEET: {
      id: 'supplierProducts',
      name: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.SUPPLIER_PRODUCTS_SHEET_NAME'),
    },
    COLUMN_ID: {
      ID: 'id',
      NAME: 'name',
      PACKAGING_NAME_1: 'packagingName1',
      PACKAGING_NAME_2: 'packagingName2',
      PACKAGING_NAME_3: 'packagingName3',
    },
    COLUMNS_CONFIG: normalizedColumns,
  };
};

const getIngredientsSheetConfig = () => {
  const normalizedColumns = normalizeKeys(getCommonColumns());

  return {
    SHEET: {
      id: 'ingredients',
      name: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.INGREDIENTS_SHEET_NAME'),
    },
    ...getCommonSheetConfig(normalizedColumns),
  };
};

const getRecipesSheetConfig = () => {
  const normalizedColumns = normalizeKeys(getCommonColumns());

  return {
    SHEET: {
      id: 'recipes',
      name: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.RECIPES_SHEET_NAME'),
    },
    ...getCommonSheetConfig(normalizedColumns),
  };
};

const getProductsSheetConfig = () => {
  const normalizedColumns = normalizeKeys(getCommonColumns());

  return {
    SHEET: {
      id: 'products',
      name: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.PRODUCTS_SHEET_NAME'),
    },
    ...getCommonSheetConfig(normalizedColumns),
  };
};

const getCategoriesSheetConfig = () => {
  const columns = {
    ID: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.ID_COLUMN'),
    NAME: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.NAME_COLUMN'),
    TYPE: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.TYPE_COLUMN'),
  };

  const normalizedColumns = normalizeKeys(columns);

  return {
    SHEET: {
      id: 'categories',
      name: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.CATEGORIES_SHEET_NAME'),
    },
    COLUMN_ID: {
      ID: 'id',
      NAME: 'name',
      TYPE: 'type',
    },
    COLUMNS_CONFIG: normalizedColumns,
  };
};

const getSubCategoriesSheetConfig = () => {
  const columns = {
    ID: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.ID_COLUMN'),
    NAME: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.NAME_COLUMN'),
    TYPE: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.TYPE_COLUMN'),
  };

  const normalizedColumns = normalizeKeys(columns);

  return {
    SHEET: {
      id: 'subCategories',
      name: i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.SUB_CATEGORIES_SHEET_NAME'),
    },
    COLUMN_ID: {
      ID: 'id',
      NAME: 'name',
      TYPE: 'type',
    },
    COLUMNS_CONFIG: normalizedColumns,
  };
};

/** Normalized column ids */

const getNormalizedSupplierProductColumnIds = () => ({
  ID: {
    key: 'id',
    normalizedKey: 'id',
  },
  NAME: {
    key: 'name',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.NAME_COLUMN'),
    ),
  },
  PACKAGING_NAME_1: {
    key: 'packagingName1',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.PACKAGING_NAME_1_COLUMN'),
    ),
  },
  PACKAGING_NAME_2: {
    key: 'packagingName2',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.PACKAGING_NAME_2_COLUMN'),
    ),
  },
  PACKAGING_NAME_3: {
    key: 'packagingName3',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.PACKAGING_NAME_3_COLUMN'),
    ),
  },
});

const getNormalizedEntitiesAndProductsColumnIds = () => ({
  ID: {
    key: 'id',
    normalizedKey: 'id',
  },
  NAME: {
    key: 'name',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.NAME_COLUMN'),
    ),
  },
});

const getNormalizedCategoriesColumnIds = () => ({
  ID: {
    key: 'id',
    normalizedKey: 'id',
  },
  NAME: {
    key: 'name',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.NAME_COLUMN'),
    ),
  },
  TYPE: {
    key: 'type',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.TYPE_COLUMN'),
    ),
  },
});

const getNormalizedSubCategoriesColumnIds = () => ({
  ID: {
    key: 'id',
    normalizedKey: 'id',
  },
  NAME: {
    key: 'name',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.NAME_COLUMN'),
    ),
  },
  TYPE: {
    key: 'type',
    normalizedKey: normalizeStringValue(
      i18next.t('_BACKEND_TRANSLATIONS.TRANSLATIONS.TYPE_COLUMN'),
    ),
  },
});

export default {
  getSupplierProductSheetConfig,
  getIngredientsSheetConfig,
  getRecipesSheetConfig,
  getProductsSheetConfig,
  getCategoriesSheetConfig,
  getSubCategoriesSheetConfig,
  getNormalizedSupplierProductColumnIds,
  getNormalizedEntitiesAndProductsColumnIds,
  getNormalizedCategoriesColumnIds,
  getNormalizedSubCategoriesColumnIds,
};
